import { range } from "es-toolkit";
import { type MouseEvent } from "react";

import { Skeleton } from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import type { useNodeTemplateCategories } from "../api";
import type { NodeTemplatesSearchCriteria } from "../types";
import { Filter, FilterButton, FilterButtonGroup, FilterHeading, FilterIcon } from "./shared";

type Category = ReturnType<typeof useNodeTemplateCategories>["categories"][0];

type Props = {
  querying: boolean;
  category?: Category["name"];
  categories: Category[];
  onChange: (newCriteria: NodeTemplatesSearchCriteria) => void;
};

const allCategory: Category = { title: "All", name: "" };

export const Categories = ({ category = "", categories, onChange, querying }: Props) => {
  const formatMessage = useFormatMessage(formatStrings);

  const handleChange = (_event: MouseEvent<HTMLElement>, selection: string) =>
    onChange({ category: selection === null ? category || undefined : selection });

  return (
    <Filter data-test="categories-container">
      <FilterHeading>{formatMessage("categories")}</FilterHeading>
      {querying ? (
        range(0, 6).map((key) => <CategoryCardSkeleton key={key} />)
      ) : (
        <FilterButtonGroup value={category} exclusive orientation="vertical" onChange={handleChange}>
          {[allCategory, ...categories.filter(({ name }) => name)].map(({ title, name }) => (
            <FilterButton key={title} value={name} size="small" disableRipple data-test="category">
              <FilterIcon icon="tick" />
              {title}
            </FilterButton>
          ))}
        </FilterButtonGroup>
      )}
    </Filter>
  );
};

export const CategoryCardSkeleton = () => <Skeleton variant="rounded" height={24} data-test="category-skeleton" />;

const formatStrings = {
  categories: {
    en: "Categories",
  },
} satisfies FormatMessages;
